import { useState, useCallback } from "react";
import { updateProfile as apiUpdateProfile } from "@/lib/api/user";
import { useAppContext } from "@/providers";
import type { PartnerProfile, UpdateProfileInput } from "@/types/user";

export interface UseProfileReturn {
  profile: PartnerProfile | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  updateProfile: (data: UpdateProfileInput) => Promise<boolean>;
  uploadAvatar: (file: File) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useProfile(): UseProfileReturn {
  const { session, refreshMe } = useAppContext();
  // Initialize directly from the already-fetched session user — no extra API call
  const [profile, setProfile] = useState<PartnerProfile | null>(
    (session.user as unknown as PartnerProfile) ?? null
  );
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // refetch re-runs the session bootstrap (already deduplicated in AppContext)
  const fetchProfile = useCallback(async () => {
    await refreshMe();
    setProfile((session.user as unknown as PartnerProfile) ?? null);
  }, [refreshMe, session.user]);

  const updateProfile = useCallback(
    async (data: UpdateProfileInput): Promise<boolean> => {
      try {
        setSaving(true);
        const response = await apiUpdateProfile(data);
        if (response.success && response.data) {
          setProfile(response.data);
          return true;
        }
        setError(response.message ?? "Failed to update profile");
        return false;
      } catch {
        setError("An unexpected error occurred");
        return false;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const uploadAvatar = useCallback(
    async (file: File): Promise<boolean> => {
      try {
        setSaving(true);
        const response = await apiUpdateProfile({ avatar: file });
        if (response.success && response.data) {
          setProfile(response.data);
          return true;
        }
        setError(response.message ?? "Failed to upload avatar");
        return false;
      } catch {
        setError("An unexpected error occurred");
        return false;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  return {
    profile,
    loading,
    error,
    saving,
    updateProfile,
    uploadAvatar,
    refetch: fetchProfile,
  };
}
